import 'babel-polyfill';
import React, { Component } from 'react';
import './App.css';
import PropTypes from 'prop-types';
import {
  withStyles,
  createMuiTheme,
  ThemeProvider,
} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import * as d3 from 'd3';
import EnergySourceSelector from './components/energySourceSelector';
import GraphContainer from './components/graphContainer';
import DateSelector from './components/dateSelector';
import Mainsite from './components/mainsite';
import Copyright from './components/copyright';
import Promise from 'promise-polyfill';

// To add to window
if (!window.Promise) {
  window.Promise = Promise;
}

const breakpointTheme = createMuiTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      tablet: 768,
      md: 960,
      lg: 1280,
      lg1366: 1366,
      lg1440: 1440,
      lg1536: 1536,
      lg1660: 1660,
      xl: 1920,
    },
  },
});

function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    width: 'calc(100%-50px)',
    top: `${top}px`,
    left: `${left}px`,
    transform: `translate(-${top / 2}px, -${left / 2}px)`,
  };
}

function getDateString(myDate) {
  return (
    myDate.getFullYear().toString() +
    ('0' + (myDate.getMonth() + 1)).slice(-2) +
    ('0' + myDate.getDate()).slice(-2)
  );
}

const styles = (theme) => ({
  root: {
    padding: '10px',
    [theme.breakpoints.down('xs')]: {
      paddingBottom: '80px',
    },
  },
  media: {
    height: '100px',
    backgroundSize: 'contain',
  },
  spaceBetween: {
    justifyContent: 'space-between',
  },
  hooverable: {
    textDecoration: 'none',
    '&:hover': {
      color: 'white!important',
      cursor: 'pointer',
    },
  },
  appTitle: {
    fontSize: '20px',
    fontWeight: 700,
    textAlign: 'left',
  },
  latestUpdateContainer: {
    color: '#9e9e9e',
    verticalAlign: 'middle',
    lineHeight: '30px',
    marginRight: '10px',
    display: 'inline-block',
    [theme.breakpoints.up('md')]: {
      marginBottom: '30px',
    },
    [theme.breakpoints.up('lg1566')]: {
      marginBottom: '0px',
    },
  },
  socialMediaBlock: {
    position: 'absolute',
    width: '190px',
    right: '5px',
    top: 0,
  },
  bottomUpdateCopyrightBlock: {
    marginTop: '10px',
    [theme.breakpoints.up('sm')]: {
      marginBottom: '80px',
    },
  },
  modalPaper: {
    position: 'absolute',
    //width: theme.spacing() * 50,
    width: 'calc(100% - 50px)',
    top: '50px',
    left: '50px',
    //height: '80vh',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing() * 1,
    //[theme.breakpoints.down('sm')]: {
    //  height: '90vh',
    //},
  },
  modalContainer: {
    width: '100%',
    height: '100%',
    maxHeight: '90vh',
    padding: theme.spacing() * 2,
    overflowX: 'hidden',
    overflowY: 'auto',
  },
});

const NUMBER_OF_DAYS_LOADED = 8;
const NUMBER_OF_DAYS_NARROW = 4;
const NUMBER_OF_YEARS_NARROW = 4;

class App extends Component {
  constructor(props) {
    super(props);

    this.loadData = this.loadData.bind(this);
    this.updateDynamicData = this.updateDynamicData.bind(this);
    this.loadStaticData = this.loadStaticData.bind(this);
    this.updateStaticData = this.updateStaticData.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.setNewDate = this.setNewDate.bind(this);

    this.displayCalendar = this.displayCalendar.bind(this);
    this.updateComponents = this.updateComponents.bind(this);
    this.hideModal = this.hideModal.bind(this);
  }

  state = {
    dataLoaded: false,
    dataLoading: false,
    staticDataLoaded: false,
    componentsUpdated: false,
    daySelected: d3.timeFormat('%Y%m%d')(new Date()),
    //daySelected: "20200609",
    previousDaySelected: d3.timeFormat('%Y%m%d')(new Date()),
    //previousDaySelected: "20200608",
    yearSelected: new Date().getFullYear() - 1,
    //yearSelected: 2017,
    currentYear: parseInt(d3.timeFormat('%Y')(new Date())),
    currentMonth: parseInt(d3.timeFormat('%m')(new Date())),
    finalYear:
      parseInt(d3.timeFormat('%m')(new Date())) > 2
        ? parseInt(d3.timeFormat('%Y')(new Date()))
        : parseInt(d3.timeFormat('%Y')(new Date())) - 1,
    totalNumberOfYears: parseInt(d3.timeFormat('%Y')(new Date())) - 2014 + 1,
    currentTech: {
      solar: true,
      wind: true,
      windOffshore: true,
    },
    previousTech: {
      solar: true,
      wind: true,
      windOffshore: true,
    },
    currentMode: 'realtime',
    currentFilter: 'energy',
    previousFilter: 'energy',
    data: {
      production: {
        solar: [],
        wind: [],
        windOffshore: [],
      },
      productionWeek: {
        solar: [],
        wind: [],
        windOffshore: [],
      },
      productionYear: {
        solar: [],
        wind: [],
        windOffshore: [],
      },
      installed: {
        solar: [],
        wind: [],
        windOffshore: [],
      },
      installedYear: {
        solar: [],
        wind: [],
        windOffshore: [],
      },
      demand: [],
      demandWeek: [],
      demandYear: [],
      statistics: {},
      calendarStats: {},
      inhabitants: [],
    },
    staticData: {
      surfaces: [],
    },
    formattedData: {
      times: [],
      yearTimes: [],
      productionWeek: [],
      productionWeekTotals: [],
      productionYear: [],
      productionYearTotals: [],
      demandWeek: [],
      demandYear: [],
      installedYear: [],
      hasForecast: false,
    },
    graphDisplay: {
      numberDaysDisplayed: 8,
      daySliceFactor: 1,
      numberYearsDisplayed: 100,
      yearSliceFactor: 1,
    },
    selectedDate: new Date(),
    //selectedDate: new Date("2020-06-09"),
    currentDateTime: '', //used for step management, set with times[i], also used in timeplot for checking forecast
    colorArray: [],
    activeStep: 10,
    maxValue: 100,
    showCalendar: false,
    calendarMode: 'realtime',
    screenSizeToggle: false,
    windowWidth: window.innerWidth,
    lang: 'nl',

    pageView: '',
  };

  hideModal() {
    if (this.state.pageView !== '') {
      this.setState({ pageView: '' });
    }
  }

  componentDidMount() {
    var url = window.location.href; //for current url
    var pageview = /pageview=([^&]+)/.exec(url); // Value is in [1] ('384' in our case)
    //if (pageview != null){
    //  alert ("for url "+url+" the pageview is "+pageview[1])
    //}
    this.setState({
      pageView:
        pageview != null &&
        (pageview[1] === 'stats' || pageview[1] === 'provinces')
          ? pageview[1]
          : 'records',
    });
    this.handleResize();
    this.loadData();
    this.loadStaticData();

    window.addEventListener('resize', this.handleResize);

    if (process.env.NODE_ENV === 'production') {
      var _mtm = (window._mtm = window._mtm || []);
      _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });
      var d = document,
        g = d.createElement('script'),
        s = d.getElementsByTagName('script')[0];
      g.async = true;
      g.src = process.env.REACT_APP_MATOMO_BASE_URL;
      s.parentNode.insertBefore(g, s);
    }
  }
  /*
  changeLanguage = (language) => {
    if (this.state.lang !== language) {
      this.setState({ lang: language })
    }
  }
  */

  handleResize() {
    this.setState((previousState, props) => {
      const totalNumberOfYears = parseInt(this.state.finalYear) - 2014 + 1;
      let yearNarrowSliceFactor;
      if (this.state.currentMonth <= 2) {
        yearNarrowSliceFactor = NUMBER_OF_YEARS_NARROW / totalNumberOfYears;
      } else {
        // we have the NUMBER_OF_YEARS_NARROW -1 + current year
        const totalNumberOfMonthsLoaded =
          12 * (totalNumberOfYears - 1) + (this.state.currentMonth - 2);
        const totalMonthsDisplayed =
          12 * (NUMBER_OF_YEARS_NARROW - 1) + this.state.currentMonth - 2;
        yearNarrowSliceFactor =
          totalMonthsDisplayed / totalNumberOfMonthsLoaded;
      }

      let newGraphDisplayConfiguration;
      if (window.innerWidth >= 960) {
        newGraphDisplayConfiguration = {
          numberDaysDisplayed: 8,
          daySliceFactor: 1,
          numberYearsDisplayed: totalNumberOfYears,
          yearSliceFactor: 1,
        };
      } else {
        newGraphDisplayConfiguration = {
          numberDaysDisplayed: NUMBER_OF_DAYS_NARROW,
          daySliceFactor: NUMBER_OF_DAYS_NARROW / NUMBER_OF_DAYS_LOADED,
          numberYearsDisplayed: NUMBER_OF_YEARS_NARROW,
          yearSliceFactor: yearNarrowSliceFactor,
        };
      }
      return {
        graphDisplay: newGraphDisplayConfiguration,
        windowWidth: window.innerWidth,
      };
    });
  }

  loadData() {
    const daySelected = this.state.daySelected;
    // pointer to state to adapt it in the state, needs to be done only once
    let selectedDate = this.state.selectedDate;
    //selectedDate.setHours(selectedDate.getHours() -1);
    const daySelectedMinusOne = getDateString(
      d3.timeDay.offset(this.state.selectedDate, -1),
    );
    const daySelectedMinusTwo = getDateString(
      d3.timeDay.offset(this.state.selectedDate, -2),
    );
    const daySelectedMinusThree = getDateString(
      d3.timeDay.offset(this.state.selectedDate, -3),
    );
    const daySelectedMinusFour = getDateString(
      d3.timeDay.offset(this.state.selectedDate, -4),
    );
    const daySelectedMinusFive = getDateString(
      d3.timeDay.offset(this.state.selectedDate, -5),
    );
    const daySelectedMinusSix = getDateString(
      d3.timeDay.offset(this.state.selectedDate, -6),
    );
    const daySelectedMinusSeven = getDateString(
      d3.timeDay.offset(this.state.selectedDate, -7),
    );

    const yearSelected = this.state.daySelected.substring(0, 4);
    //const nextYear = parseInt(this.state.yearSelected) + 1;
    const totalNumberOfYears = parseInt(this.state.finalYear) - 2014 + 1;
    //let nextYearNavigationDisabled = false;

    const updateData = (data) => {
      this.updateDynamicData(data);
    };

    let promises = [
      d3.csv('./data/inhabitants/inhabitants_' + yearSelected + '.csv'),

      d3.csv(
        './data/realtime/realtime_solar_' + daySelectedMinusSeven + '.csv',
      ),
      d3.csv('./data/realtime/realtime_solar_' + daySelectedMinusSix + '.csv'),
      d3.csv('./data/realtime/realtime_solar_' + daySelectedMinusFive + '.csv'),
      d3.csv('./data/realtime/realtime_solar_' + daySelectedMinusFour + '.csv'),
      d3.csv(
        './data/realtime/realtime_solar_' + daySelectedMinusThree + '.csv',
      ),
      d3.csv('./data/realtime/realtime_solar_' + daySelectedMinusTwo + '.csv'),
      d3.csv('./data/realtime/realtime_solar_' + daySelectedMinusOne + '.csv'),
      d3.csv('./data/realtime/realtime_solar_' + daySelected + '.csv'),

      d3.csv('./data/realtime/realtime_wind_' + daySelectedMinusSeven + '.csv'),
      d3.csv('./data/realtime/realtime_wind_' + daySelectedMinusSix + '.csv'),
      d3.csv('./data/realtime/realtime_wind_' + daySelectedMinusFive + '.csv'),
      d3.csv('./data/realtime/realtime_wind_' + daySelectedMinusFour + '.csv'),
      d3.csv('./data/realtime/realtime_wind_' + daySelectedMinusThree + '.csv'),
      d3.csv('./data/realtime/realtime_wind_' + daySelectedMinusTwo + '.csv'),
      d3.csv('./data/realtime/realtime_wind_' + daySelectedMinusOne + '.csv'),
      d3.csv('./data/realtime/realtime_wind_' + daySelected + '.csv'),

      d3.csv(
        './data/realtime/realtime_wind_offshore_' +
          daySelectedMinusSeven +
          '.csv',
      ),
      d3.csv(
        './data/realtime/realtime_wind_offshore_' +
          daySelectedMinusSix +
          '.csv',
      ),
      d3.csv(
        './data/realtime/realtime_wind_offshore_' +
          daySelectedMinusFive +
          '.csv',
      ),
      d3.csv(
        './data/realtime/realtime_wind_offshore_' +
          daySelectedMinusFour +
          '.csv',
      ),
      d3.csv(
        './data/realtime/realtime_wind_offshore_' +
          daySelectedMinusThree +
          '.csv',
      ),
      d3.csv(
        './data/realtime/realtime_wind_offshore_' +
          daySelectedMinusTwo +
          '.csv',
      ),
      d3.csv(
        './data/realtime/realtime_wind_offshore_' +
          daySelectedMinusOne +
          '.csv',
      ),
      d3.csv('./data/realtime/realtime_wind_offshore_' + daySelected + '.csv'),

      // https://stackoverflow.com/questions/13870265/read-csv-tsv-with-no-header-line-in-d3
      d3.text(
        './data/realtime/realtime_demand_' + daySelectedMinusSeven + '.csv',
      ),
      d3.text(
        './data/realtime/realtime_demand_' + daySelectedMinusSix + '.csv',
      ),
      d3.text(
        './data/realtime/realtime_demand_' + daySelectedMinusFive + '.csv',
      ),
      d3.text(
        './data/realtime/realtime_demand_' + daySelectedMinusFour + '.csv',
      ),
      d3.text(
        './data/realtime/realtime_demand_' + daySelectedMinusThree + '.csv',
      ),
      d3.text(
        './data/realtime/realtime_demand_' + daySelectedMinusTwo + '.csv',
      ),
      d3.text(
        './data/realtime/realtime_demand_' + daySelectedMinusOne + '.csv',
      ),
      d3.text('./data/realtime/realtime_demand_' + daySelected + '.csv'),
    ];

    let year;
    for (year = 2014; year <= this.state.finalYear; year++) {
      promises.push(
        d3.csv('./data/historical/historical_solar_' + year + '.csv'),
      );
    }
    for (year = 2014; year <= this.state.finalYear; year++) {
      promises.push(
        d3.csv('./data/historical/historical_wind_' + year + '.csv'),
      );
    }
    for (year = 2014; year <= this.state.finalYear; year++) {
      promises.push(
        d3.csv('./data/historical/historical_wind_offshore_' + year + '.csv'),
      );
    }
    for (year = 2014; year <= this.state.finalYear; year++) {
      promises.push(
        d3.text('./data/historical/historical_load_' + year + '.csv'),
      );
    }
    for (year = 2014; year <= this.state.finalYear; year++) {
      promises.push(
        d3.csv('./data/historical/installed_capacity_solar_' + year + '.csv'),
      );
    }
    for (year = 2014; year <= this.state.finalYear; year++) {
      promises.push(
        d3.csv('./data/historical/installed_capacity_wind_' + year + '.csv'),
      );
    }
    for (year = 2014; year <= this.state.finalYear; year++) {
      promises.push(
        d3.csv(
          './data/historical/installed_capacity_wind_offshore_' + year + '.csv',
        ),
      );
    }

    for (let i = 7; i >= 0; i--) {
      const day = getDateString(d3.timeDay.offset(this.state.selectedDate, -i));
      promises.push(d3.text(`./data/realtime/realtime_price_${day}.csv`));
    }

    return new Promise(function (resolve, reject) {
      Promise.all(promises)
        .then(function (d, error) {
          if (error) {
            console.log(error);
          } else {
            //format demand
            d[25] = d3.csvParseRows(d[25]);
            d[26] = d3.csvParseRows(d[26]);
            d[27] = d3.csvParseRows(d[27]);
            d[28] = d3.csvParseRows(d[28]);
            d[29] = d3.csvParseRows(d[29]);
            d[30] = d3.csvParseRows(d[30]);
            d[31] = d3.csvParseRows(d[31]);
            d[32] = d3.csvParseRows(d[32]);

            let counter;
            const counterStart = 32 + 3 * totalNumberOfYears + 1;
            const counterEnd = 32 + 4 * totalNumberOfYears;
            for (counter = counterStart; counter <= counterEnd; counter++) {
              d[counter] = d3.csvParseRows(d[counter]);
            }

            // format prices
            const lastPricesIndex = d.length-1;
            if (d[lastPricesIndex]) {
              for (let i = lastPricesIndex; i >= lastPricesIndex - 7; i--) {
                d[i] = d3.csvParse(d[i], (row) => {
                  const value = parseFloat(row[0], 10);

                  if (!isNaN(value)) {
                    return {
                      datetime: row.datetime,
                      value,
                    };
                  }
                });
              }
            }

            return d;
          }
        })
        .then((resultData) => resolve(updateData(resultData)));
    });
  }
  updateDynamicData(resultData) {
    const totalNumberOfYears = parseInt(this.state.finalYear) - 2014 + 1;

    this.setState((previousState, props) => {
      const pricesWeek = resultData.slice(-8); // since we added "day prices" at the end of the array we can grab them like that. If we change the order/add other data we will need a more robust approach
      return {
        data: {
          ...previousState.data,
          inhabitants: resultData[4],
          production: {
            solar: resultData[8],
            wind: resultData[16],
            windOffshore: resultData[24],
          },
          demand: resultData[32],
          productionWeek: {
            solar: [
              resultData[1],
              resultData[2],
              resultData[3],
              resultData[4],
              resultData[5],
              resultData[6],
              resultData[7],
              resultData[8],
            ],
            wind: [
              resultData[9],
              resultData[10],
              resultData[11],
              resultData[12],
              resultData[13],
              resultData[14],
              resultData[15],
              resultData[16],
            ],
            windOffshore: [
              resultData[17],
              resultData[18],
              resultData[19],
              resultData[20],
              resultData[21],
              resultData[22],
              resultData[23],
              resultData[24],
            ],
          },
          demandWeek: [
            resultData[25],
            resultData[26],
            resultData[27],
            resultData[28],
            resultData[29],
            resultData[30],
            resultData[31],
            resultData[32],
          ],
          pricesWeek,
          productionYear: {
            solar: resultData.slice(32 + 1, 32 + totalNumberOfYears + 1),
            wind: resultData.slice(
              32 + totalNumberOfYears + 1,
              32 + 2 * totalNumberOfYears + 1,
            ),
            windOffshore: resultData.slice(
              32 + 2 * totalNumberOfYears + 1,
              32 + 3 * totalNumberOfYears + 1,
            ),
          },
          demandYear: resultData.slice(
            32 + 3 * totalNumberOfYears + 1,
            32 + 4 * totalNumberOfYears + 1,
          ),
          installedYear: {
            solar: resultData.slice(
              32 + 4 * totalNumberOfYears + 1,
              32 + 5 * totalNumberOfYears + 1,
            ),
            wind: resultData.slice(
              32 + 5 * totalNumberOfYears + 1,
              32 + 6 * totalNumberOfYears + 1,
            ),
            windOffshore: resultData.slice(
              32 + 6 * totalNumberOfYears + 1,
              32 + 7 * totalNumberOfYears + 1,
            ),
          },
        },
        dataLoading: true,
        dataLoaded: true,
      };
    });
  }

  loadStaticData() {
    const updateData = (data) => {
      this.updateStaticData(data);
    };
    return new Promise(function (resolve, reject) {
      Promise.all([d3.csv('./map_data/surfaces.csv')]).then(
        function (d, error) {
          if (error) {
            console.log(error);
          } else {
            resolve(updateData(d));
          }
        },
      );
    });
  }

  updateStaticData(resultData) {
    this.setState((previousState, props) => {
      return {
        staticData: {
          ...previousState.staticData,
          surfaces: resultData[0],
        },
        staticDataLoaded: true,
      };
    });
  }

  changeCurrentDaySelected = (selectedDate) => {
    if (this.state.daySelected !== selectedDate) {
      const newDate = new Date(
        selectedDate.substring(0, 4) +
          '/' +
          selectedDate.substring(4, 6) +
          '/' +
          selectedDate.substring(6, 8),
      );
      this.setState((previousState, props) => {
        let updatedStep = parseInt(d3.timeFormat('%H')(new Date()));
        if (previousState.playing) {
          if (
            previousState.currentTech.solar &&
            !previousState.currentTech.wind
          )
            updatedStep = 6;
          else updatedStep = 0;
        }

        return {
          daySelected: selectedDate,
          selectedDate: newDate,
          componentsUpdated: false,
          dataLoaded: false,
          dataLoading: false,
          showCalendar: false,

          activeStep: updatedStep,
          justToggled: true,
        };
      });
    }
  };

  setFilter = (e) => {
    const target = e.currentTarget;
    this.setState({
      currentFilter: target.dataset.filter,
    });
  };

  setNewDate = (selectedDate) => {
    const newDate = new Date(selectedDate);
    const daySelected = d3.timeFormat('%Y%m%d')(newDate);
    if (daySelected === this.state.daySelected) {
      if (this.state.currentMode === 'realtime') {
        this.displayCalendar(false);
      }
    } else {
      // 20181116 ensure that for each new day the timer gets reset to ensure the correct number of max step
      this.setState((previousState, props) => {
        let updatedStep = parseInt(d3.timeFormat('%H')(new Date()));
        if (previousState.playing) {
          //if (previousState.currentTech==='solar')
          if (
            previousState.currentTech.solar &&
            !previousState.currentTech.wind
          )
            updatedStep = 6;
          else updatedStep = 0;
        }

        return {
          daySelected: daySelected,
          selectedDate: newDate,
          componentsUpdated: false,
          dataLoaded: false,
          dataLoading: false,
          showCalendar: false,

          currentMode: 'realtime',
          activeStep: updatedStep,
        };
      });
    }
  };

  displayCalendar = (boolShowCalendar, calendarMode) => {
    this.setState({
      showCalendar: boolShowCalendar,
      calendarMode: calendarMode,
    });
  };

  setSelectedEnergySource = (e) => {
    const target = e.target;
    this.setState((previousState, props) => {
      let newTech = { ...previousState.currentTech };

      switch (target.dataset.energy_source) {
        case 'solar':
          if (!newTech.solar || newTech.wind || newTech.windOffshore) {
            newTech.solar = !newTech.solar;
          } else {
            // do nothing or show an alert
          }
          break;
        case 'wind':
          if (newTech.solar || !newTech.wind || newTech.windOffshore) {
            newTech.wind = !newTech.wind;
          } else {
            // do nothing or show an alert
          }
          break;
        case 'windOffshore':
          if (newTech.solar || newTech.wind || !newTech.windOffshore) {
            newTech.windOffshore = !newTech.windOffshore;
          } else {
            // do nothing or show an alert
          }
          break;
        default:
      }
      return {
        currentTech: newTech,
        componentsUpdated: false,
        currentFilter: 'energy',
      };
    });
  };

  componentDidUpdate() {
    if (this.state.dataLoaded) {
      let stateupdated =
        JSON.stringify(this.state.currentTech) ===
          JSON.stringify(this.state.previousTech) &&
        this.state.currentFilter === this.state.previousFilter &&
        this.state.daySelected === this.state.previousDaySelected;
      if (!stateupdated || !this.state.componentsUpdated)
        this.updateComponents();
    } else {
      if (!this.state.dataLoading) {
        this.loadData();
      }
    }
  }

  sumObjectsByKey(...objs) {
    return objs.reduce((a, b) => {
      for (let k in b) {
        if (Object.prototype.hasOwnProperty.call(b, k))
          a[k] = (parseFloat(a[k]) || 0) + parseFloat(b[k]);
      }
      return a;
    }, {});
  }

  updateComponents() {
    let techs = [];
    const currentTech = this.state.currentTech;
    const currentFilter = this.state.currentFilter;
    const currentMode = this.state.currentMode;

    let techData = this.state.data;
    let staticData = this.state.staticData;
    let inhabitants = d3
      .nest()
      .key(function (k) {
        return k.nis;
      })
      .rollup(function (v) {
        return v[0].value;
      })
      .object(techData.inhabitants);
    let surfaces = d3
      .nest()
      .key(function (k) {
        return k.nis;
      })
      .rollup(function (v) {
        return v[0].value / 1000000;
      })
      .object(staticData.surfaces);

    let colorArray = [];

    if (currentTech.windOffshore) {
      techs.push('windOffshore');
    }
    if (!currentTech.solar) {
      colorArray = [
        '#bfd6e2',
        '#abc8d7',
        '#98bacc',
        '#87acc0',
        '#779eb3',
        '#6890a5',
        '#5a8197',
        '#4e7488',
        '#43667a',
        '#39596a',
        '#304c5b',
      ];
      if (currentTech.wind) {
        techs.push('wind');
      }
    } else {
      if (currentTech.wind) {
        techs.push('wind');
        colorArray = [
          '#b7dcad',
          '#a2d094',
          '#8cc47c',
          '#7ab769',
          '#67aa58',
          '#579d4a',
          '#488f3e',
          '#3b8134',
          '#2e742d',
          '#236628',
          '#1a5924',
        ];
      } else {
        if (currentTech.windOffshore)
          colorArray = [
            '#b7dcad',
            '#a2d094',
            '#8cc47c',
            '#7ab769',
            '#67aa58',
            '#579d4a',
            '#488f3e',
            '#3b8134',
            '#2e742d',
            '#236628',
            '#1a5924',
          ];
        else
          colorArray = [
            '#fff292',
            '#ffeb81',
            '#ffe471',
            '#ffdc5e',
            '#ffd349',
            '#ffcc33',
            '#fbbb3b',
            '#f7aa41',
            '#f29945',
            '#ed8749',
            '#e8754c',
          ];
      }
      techs.push('solar');
    }

    let d = [];
    techs.map(function (tech, i) {
      if (techData.production[tech].length > 0) {
        techData.production[tech].map(function (r) {
          let rowDateTime = r.datetime;
          Object.keys(r)
            .filter((key) => key !== 'datetime')
            .map((nis) => {
              let v = {
                tech: tech,
                datetime: rowDateTime,
                nis: nis,
                value: parseFloat(r[nis]),
              };
              d.push(v);
              return true;
            });
          return true;
        });
      }
      return true;
    });
    d = [].concat.apply([], d);

    let techWeekData = [];
    techs.map(function (tech, i) {
      techData.productionWeek[tech].map(function (day, j) {
        techData.productionWeek[tech][j].map(function (r) {
          let rowDateTime = r.datetime;
          Object.keys(r)
            .filter((key) => key !== 'datetime')
            .map((nis) => {
              let v = {
                tech: tech,
                datetime: rowDateTime,
                nis: nis,
                value: parseFloat(r[nis]),
              };
              techWeekData.push(v);
              return true;
            });
          return true;
        });
      });
      return true;
    });
    techWeekData = [].concat.apply([], techWeekData);

    let techYearData = [];
    techs.map(function (tech, i) {
      techData.productionYear[tech].map(function (day, j) {
        techData.productionYear[tech][j].map(function (r) {
          let rowDateTime = r.datetime;
          Object.keys(r)
            .filter((key) => key !== 'datetime')
            .map((nis) => {
              let v = {
                tech: tech,
                datetime: rowDateTime,
                nis: nis,
                value: parseFloat(r[nis]),
              };
              techYearData.push(v);
              return true;
            });
          return true;
        });
      });
      return true;
    });
    techYearData = [].concat.apply([], techYearData);

    let demandWeekData = this.state.data.demandWeek;
    let demandWeek = [];
    demandWeekData.map(function (day, j) {
      demandWeekData[j].map(function (r) {
        let v = {
          datetime: r[0],
          value: parseFloat(r[1]),
        };
        demandWeek.push(v);
        return true;
      });
    });
    demandWeek = [].concat.apply([], demandWeek);

    let demandYearData = this.state.data.demandYear;
    let demandYear = [];
    demandYearData.map(function (day, j) {
      demandYearData[j].map(function (r) {
        let v = {
          datetime: r[0],
          value: parseFloat(r[1]),
        };
        demandYear.push(v);
        return true;
      });
      return true;
    });
    demandYear = [].concat.apply([], demandYear);

    let times = d3
      .set(d, function (k) {
        return k.datetime;
      })
      .values()
      .map(function (k) {
        const kYear = k.substring(0, 4);
        const kMonth = parseInt(k.substring(5, 7)) - 1;
        const kDay = k.substring(8, 10);
        const kHour = k.substring(11, 13);
        const kMinute = k.substring(14, 16);
        const kSecond = k.substring(17, 19);
        const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
        return kDate;
      });

    let yearTimes = d3
      .set(techYearData, function (k) {
        return k.datetime;
      })
      .values()
      .map(function (k) {
        const kYear = k.substring(0, 4);
        const kMonth = parseInt(k.substring(5, 7)) - 1;
        const kDay = k.substring(8, 10);
        const kHour = k.substring(11, 13);
        const kMinute = k.substring(14, 16);
        const kSecond = k.substring(17, 19);
        const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
        return kDate;
      });

    let productionWeek = d3
      .nest()
      .key(function (k) {
        return k.tech;
      })
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value / 1000;
        });
      })
      .object(techWeekData);

    let productionYear = d3
      .nest()
      .key(function (k) {
        return k.tech;
      })
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value / 1000;
        });
      })
      .object(techYearData);

    let productionWeekTotals = d3
      .nest()
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value / 1000;
        });
      })
      .object(techWeekData);

    let productionYearTotals = d3
      .nest()
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value / 1000;
        });
      })
      .object(techYearData);

    let totInh = 0;
    if (currentFilter === 'inhabitants') {
      Object.keys(inhabitants).map((key) => {
        totInh += parseFloat(inhabitants[key]);
        return true;
      });
    }

    productionWeek = Object.keys(productionWeek)
      .filter(function (t) {
        return t !== 'demand';
      })
      .map(function (k) {
        return {
          name: k,
          values: Object.keys(productionWeek[k]).map(function (v) {
            let val = productionWeek[k][v];
            if (currentFilter === 'surface') {
              if (currentMode === 'realtime')
                val =
                  (val /
                    d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
                  100000;
              else
                val =
                  (val /
                    d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
                  100;
            } else if (currentFilter === 'inhabitants') {
              val = (1000000000 * val) / totInh;
            }
            const kYear = v.substring(0, 4);
            const kMonth = parseInt(v.substring(5, 7)) - 1;
            const kDay = v.substring(8, 10);
            const kHour = v.substring(11, 13);
            const kMinute = v.substring(14, 16);
            const kSecond = v.substring(17, 19);
            const kDate = new Date(
              kYear,
              kMonth,
              kDay,
              kHour,
              kMinute,
              kSecond,
            );
            return {
              datetime: kDate,
              value: val,
            };
          }),
        };
      });

    productionYear = Object.keys(productionYear)
      .filter(function (t) {
        return t !== 'demand';
      })
      .map(function (k) {
        return {
          name: k,
          values: Object.keys(productionYear[k]).map(function (v) {
            let val = productionYear[k][v];
            if (currentFilter === 'surface') {
              if (currentMode === 'realtime')
                val =
                  (val /
                    d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
                  100000;
              else
                val =
                  (val /
                    d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
                  100;
            } else if (currentFilter === 'inhabitants') {
              val = (1000000000 * val) / totInh;
            }
            const kYear = v.substring(0, 4);
            const kMonth = parseInt(v.substring(5, 7)) - 1;
            const kDay = v.substring(8, 10);
            const kHour = v.substring(11, 13);
            const kMinute = v.substring(14, 16);
            const kSecond = v.substring(17, 19);
            const kDate = new Date(
              kYear,
              kMonth,
              kDay,
              kHour,
              kMinute,
              kSecond,
            );
            return {
              datetime: kDate,
              value: val,
            };
          }),
        };
      });

    productionWeekTotals = Object.keys(productionWeekTotals).map(function (k) {
      const kYear = k.substring(0, 4);
      const kMonth = parseInt(k.substring(5, 7)) - 1;
      const kDay = k.substring(8, 10);
      const kHour = k.substring(11, 13);
      const kMinute = k.substring(14, 16);
      const kSecond = k.substring(17, 19);
      const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
      return {
        datetime: kDate,
        value: productionWeekTotals[k],
      };
    });

    productionYearTotals = Object.keys(productionYearTotals).map(function (k) {
      const kYear = k.substring(0, 4);
      const kMonth = parseInt(k.substring(5, 7)) - 1;
      const kDay = k.substring(8, 10);
      const kHour = k.substring(11, 13);
      const kMinute = k.substring(14, 16);
      const kSecond = k.substring(17, 19);
      const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
      return {
        datetime: kDate,
        value: productionYearTotals[k],
      };
    });

    let formattedDemand = d3
      .nest()
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value / 1000;
        });
      })
      .object(demandWeek);
    formattedDemand = Object.keys(formattedDemand).map(function (k) {
      const kYear = k.substring(0, 4);
      const kMonth = parseInt(k.substring(5, 7)) - 1;
      const kDay = k.substring(8, 10);
      const kHour = k.substring(11, 13);
      const kMinute = k.substring(14, 16);
      const kSecond = k.substring(17, 19);
      const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
      return {
        datetime: kDate,
        value: formattedDemand[k],
      };
    });

    let formattedDemandYear = d3
      .nest()
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value / 1000;
        });
      })
      .object(demandYear);
    formattedDemandYear = Object.keys(formattedDemandYear).map(function (k) {
      const kYear = k.substring(0, 4);
      const kMonth = parseInt(k.substring(5, 7)) - 1;
      const kDay = k.substring(8, 10);
      const kHour = k.substring(11, 13);
      const kMinute = k.substring(14, 16);
      const kSecond = k.substring(17, 19);
      const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
      return {
        datetime: kDate,
        value: formattedDemandYear[k],
      };
    });

    let installedYearData = [];
    techs.map(function (tech, i) {
      techData.installedYear[tech].map(function (day, j) {
        techData.installedYear[tech][j].map(function (r) {
          let rowDateTime = r.datetime;
          Object.keys(r)
            .filter((key) => key !== 'datetime')
            .map((nis) => {
              let v = {
                tech: tech,
                datetime: rowDateTime,
                nis: nis,
                value: parseFloat(r[nis]),
              };
              if (new Date(rowDateTime) < new Date()) installedYearData.push(v);
              return true;
            });
          return true;
        });
      });
      return true;
    });
    installedYearData = [].concat.apply([], installedYearData);
    let installedYear = d3
      .nest()
      .key(function (k) {
        return k.tech;
      })
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value / 1000;
        });
      })
      .object(installedYearData);

    installedYear = Object.keys(installedYear)
      .filter(function (t) {
        return t !== 'demand';
      })
      .map(function (k) {
        return {
          name: k,
          values: Object.keys(installedYear[k]).map(function (v) {
            let val = installedYear[k][v];
            if (currentFilter === 'surface') {
              if (currentMode === 'realtime')
                val =
                  (val /
                    d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
                  100000;
              else
                val =
                  (val /
                    d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
                  100;
            } else if (currentFilter === 'inhabitants') {
              val = (1000000000 * val) / totInh;
            }
            const kYear = v.substring(0, 4);
            const kMonth = parseInt(v.substring(5, 7)) - 1;
            const kDay = v.substring(8, 10);
            const kHour = v.substring(11, 13);
            const kMinute = v.substring(14, 16);
            const kSecond = v.substring(17, 19);
            const kDate = new Date(
              kYear,
              kMonth,
              kDay,
              kHour,
              kMinute,
              kSecond,
            );

            return {
              datetime: kDate,
              value: val,
            };
          }),
        };
      });

    const pricesWeek = []
      .concat(...this.state.data.pricesWeek)
      .map((price) => ({
        datetime: new Date(price.datetime),
        value: price.value,
      }));

    this.setState((previousState, props) => {
      let activeStep = previousState.activeStep;

      return {
        formattedData: {
          ...previousState.formattedData,
          times: times,
          yearTimes: yearTimes,
          productionWeek: productionWeek,
          productionWeekTotals: productionWeekTotals,
          productionYear: productionYear,
          productionYearTotals: productionYearTotals,
          demandWeek: formattedDemand,
          demandYear: formattedDemandYear,
          installedYear: installedYear,
          pricesWeek,
        },
        componentsUpdated: true,
        colorArray: colorArray,

        previousDaySelected: previousState.daySelected,
        previousTech: previousState.currentTech,
        previousFilter: previousState.currentFilter,
        currentDateTime: times[activeStep],
        activeStep: activeStep,
      };
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  render() {
    const { classes } = this.props;

    let backToMain = 'Terug naar home';

    return (
      <ThemeProvider theme={breakpointTheme}>
        <div className={'App ' + classes.root}>
          <Grid container spacing={0} direction={'column'} wrap="nowrap">
            <Grid item xs={12}>
              <div
                className={'darkbluegreen'}
                style={{ textAlign: 'left', fontSize: '16pt' }}
              >
                Groenestroomproductie uit zon en wind en invulling van de
                elektriciteitsvraag in Vlaanderen
              </div>
            </Grid>
            {this.state.windowWidth < 600 && (
              <Grid item xs={12}>
                <div
                  className={'bglightyellow'}
                  style={{ textAlign: 'left', fontSize: '14pt' }}
                >
                  Voor optimaal gebruik bevelen we aan je smartphone te draaien
                </div>
              </Grid>
            )}
            <Grid container item xs={12} style={{ marginBottom: '30px' }}>
              <Grid item xs={this.state.windowWidth >= 600 ? 6 : 12}>
                <EnergySourceSelector
                  currentTech={this.state.currentTech}
                  setEnergySource={this.setSelectedEnergySource}
                  dataLoaded={this.state.dataLoaded}
                  lang={this.state.lang}
                />
              </Grid>
              {this.state.windowWidth >= 600 && (
                <Grid item xs={6}>
                  <DateSelector
                    selectedDate={this.state.selectedDate}
                    selectedYear={this.state.yearSelected}
                    currentMode={this.state.currentMode}
                    changeCurrentDaySelected={this.changeCurrentDaySelected}
                    showCalendar={this.state.showCalendar}
                    displayCalendar={this.displayCalendar}
                    calendarMode={this.state.calendarMode}
                    onDateChange={this.setNewDate}
                    onYearChange={this.setNewYear}
                    onCloseClick={this.displayCalendar}
                    changeMode={this.toggleMode}
                    graphDisplay={this.state.graphDisplay}
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <GraphContainer
                currentTech={this.state.currentTech}
                currentFilter={this.state.currentFilter}
                data={this.state.formattedData}
                selectedDate={this.state.selectedDate}
                currentDateTime={this.state.currentDateTime}
                dataReady={
                  this.state.dataLoaded && this.state.componentsUpdated
                }
                windowWidth={this.state.windowWidth}
                graphDisplay={this.state.graphDisplay}
                finalYear={this.state.finalYear}
              ></GraphContainer>
            </Grid>
          </Grid>
        </div>
        <Grid container>
          <Grid item xs={4}>
            <Mainsite lang={this.state.lang} />
          </Grid>
          <Grid item xs={4}></Grid>
          <Grid item xs={4}>
            <Copyright lang={this.state.lang} />
          </Grid>
        </Grid>
      </ThemeProvider>
    );
  }
}

App.propTypes = {
  classes: PropTypes.object.isRequired,
};
export default withStyles(styles)(App);
